import { useEffect, useState, type FormEvent } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import * as api from "../api/client";
import { setSocketToken } from "../api/rooms";
import { pixelTheme } from "../theme/pixel";
import { primaryBtn, withBtnStyle } from "../theme/styles";
import { inertAttrs } from "../logic/inert";

const MIN_PASSWORD = 10;

export function ResetPasswordPage() {
  const theme = pixelTheme;
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token") ?? "";

  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!token) setError("Missing or invalid reset link.");
  }, [token]);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    if (submitting) return;
    if (!token || password.length < MIN_PASSWORD) return;
    setSubmitting(true);
    setError(null);
    try {
      const resp = await api.resetPassword(token, password);
      setSocketToken(resp.socket_token);
      navigate("/lobby", { replace: true });
    } catch (err) {
      setError(
        err instanceof api.ApiError && err.status === 401
          ? "This reset link is invalid or has expired. Request a new one."
          : err instanceof Error
            ? err.message
            : String(err),
      );
      setSubmitting(false);
    }
  }

  return (
    <div
      style={{
        minHeight: "100%",
        background: theme.pageBg,
        color: theme.ink,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        fontFamily: theme.fontBody,
        padding: 20,
      }}
    >
      <form
        onSubmit={onSubmit}
        style={{
          background: theme.panel,
          border: `2px solid ${theme.panelEdgeStrong}`,
          padding: 28,
          width: "100%",
          maxWidth: 460,
          display: "grid",
          gap: 18,
        }}
      >
        <div
          style={{
            fontFamily: theme.fontDisplay,
            fontSize: 16,
            letterSpacing: theme.letterSpacing,
            color: theme.accent,
            textAlign: "center",
          }}
        >
          CHOOSE A NEW PASSWORD
        </div>
        <label
          style={{
            display: "grid",
            gap: 6,
            fontFamily: theme.fontDisplay,
            fontSize: 11,
            letterSpacing: theme.letterSpacing,
            color: theme.inkDim,
          }}
        >
          NEW PASSWORD
          <input
            autoFocus
            type="password"
            autoComplete="new-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            disabled={submitting || !token}
            placeholder={`at least ${MIN_PASSWORD} characters`}
            className="doi-input"
            style={{
              fontFamily: theme.fontBody,
              fontSize: 20,
              padding: "8px 10px",
              background: theme.bgDeep,
              color: theme.ink,
              border: `2px solid ${theme.panelEdge}`,
            }}
          />
        </label>
        {error && (
          <div style={{ color: theme.accent, fontSize: 14 }}>{error}</div>
        )}
        <button
          type="submit"
          {...inertAttrs(submitting || !token || password.length < MIN_PASSWORD)}
          {...withBtnStyle(primaryBtn(theme, submitting), {
            fontSize: 12,
            padding: "10px 16px",
          })}
        >
          {submitting ? "…" : "SET PASSWORD"}
        </button>
        <div style={{ textAlign: "center", fontSize: 12 }}>
          <Link to="/forgot-password" style={{ color: theme.inkDim }}>
            Request a new link
          </Link>
        </div>
      </form>
    </div>
  );
}
